import React, { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useStore } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { get } from 'lodash';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import {
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
} from 'office-ui-fabric-react';
import { useBoolean } from '@uifabric/react-hooks';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleHeader from '../../components/SubModuleHeader';
import useAPI from '../../hooks/useAPI';
import { fetchContract } from '../../actions/contracts';
import CalculationFeatureCard from '../../components/CalculationFeatures/CalculationFeatureCard/CalculationFeatureCard';
import Main from '../../components/Main';
import PageTitle from '../../components/PageTitle';
import styles from './CalculationFeatures.styles';
import { CalculationFeatureForm } from '../../components/CalculationFeatures';
import { patchCalculationFeature, postCalculationFeature } from '../../actions/calculationFeatures';
import TextButton from '../../components/TextButton';

export const AddRoyaltyFeeButtonTestId = 'AddRoyaltyFeeButtonTestId';
export const CalcFeatureFormTestId = 'CalcFeatureFormTestId';

const CalculationFeatures = ({ title, continueTo = '' }) => {
  const history = useHistory();
  const { contractAddress } = useParams();
  const { isLoading, error } = useAPI(() => fetchContract(contractAddress, { useCache: false }));
  const [selectedFeature, setSelectedFeature] = useState();
  const [formTitle, setFormTitle] = useState('');
  const calcTypes = useSelector(state => get(state, 'entities.calcFeatureCalcTypes'));
  const store = useStore();

  const errorDetail = useMemo(() => {
    if (!error) {
      return null;
    }
    let errorTitle = 'Whoops!';
    let message = 'Something went wrong. Please refresh the page to try again.';
    if (error.status === 404) {
      message = 'This contract does not exist. Please try a different link.';
      errorTitle = 'Not Found';
    }
    return {
      title: errorTitle,
      message,
    };
  }, [error]);

  const [formPanelVisible, { setTrue: showFormPanel, setFalse: hideFormPanel }] = useBoolean(false);

  const onFormPanelDismissed = () => {
    hideFormPanel();
  };

  const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, null));
  const featuresApi = useAPI(
    feature =>
      feature.address
        ? patchCalculationFeature(contractAddress, feature)
        : postCalculationFeature(contractAddress, feature),
    [contract],
    {
      fetchOnMount: false,
      autoFetch: false,
      onSuccessCallback: hideFormPanel,
    },
  );

  const handleSubmit = async feature => featuresApi.fetchApi(feature).catch();
  const pushContractHistory =
    (address = '') =>
    () =>
      history.push(`/contracts/${contractAddress}${address}`);
  const handleContinue = pushContractHistory(continueTo);
  const handleCancel = pushContractHistory();

  if (isLoading || !contract) {
    return <Spinner size={SpinnerSize.medium} className={styles.loadingSpinner} />;
  }
  if (errorDetail) {
    return (
      <>
        <PageTitle>{errorDetail.title}</PageTitle>
        <Main>
          <Text>{errorDetail.message}</Text>
        </Main>
      </>
    );
  }
  const { calculationFeatures } = contract;
  const isSubmitting = false;
  const onNewFeatureClick = () => {
    setSelectedFeature(undefined);
    setFormTitle('Add Calculation Feature');
    showFormPanel();
  };
  const onCalcFeatureEditClick = feature => {
    setSelectedFeature(feature);
    setFormTitle('Edit Calculation Feature');
    showFormPanel();
  };

  const createOrAddErrorMessage = err => {
    return err.message
      ? err.message
      : 'Something went wrong when saving. Please try refreshing the page.';
  };
  return (
    <>
      {featuresApi.error && (
        <MessageBar
          isMultiline={false}
          messageBarType={MessageBarType.error}
          messageBarIconProps={{ iconName: 'Info' }}
        >
          {createOrAddErrorMessage(featuresApi.error)}
        </MessageBar>
      )}
      <PageTitle>{title}</PageTitle>
      <Main>
        {calculationFeatures &&
          calculationFeatures.length > 0 &&
          calculationFeatures.map(feature => (
            <CalculationFeatureCard
              calculationFeature={feature}
              editFeatureClicked={onCalcFeatureEditClick}
            />
          ))}
        <Module>
          <ModuleContent>
            <SubModule
              disableAccordian
              closed
              disabled={isSubmitting}
              className={clsx(
                styles.addFeatureButtonSectionHeader,
                isSubmitting && styles.addFeatureButtonSectionHeaderDisabled,
              )}
              onClick={onNewFeatureClick}
              data-testid={AddRoyaltyFeeButtonTestId}
            >
              <SubModuleHeader
                variant="large"
                extra={
                  <IconButton
                    iconProps={{ iconName: 'Add' }}
                    title="Add a Royalty Fee"
                    ariaLabel="Add a Royalty Fee"
                  />
                }
              >
                Add a Calculation Feature
              </SubModuleHeader>
            </SubModule>
          </ModuleContent>
        </Module>
        {/* BUTTONS */}
        <div className={styles.buttonContainer}>
          <TextButton
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </TextButton>
          <PrimaryButton onClick={handleContinue} disabled={isSubmitting}>
            Continue
          </PrimaryButton>
        </div>
      </Main>
      <Panel
        isOpen={formPanelVisible}
        onDismiss={onFormPanelDismissed}
        closeButtonAriaLabel="Close"
        headerText={formTitle}
        className={styles.panelHeading}
        type={PanelType.smallFluid}
        onOuterClick={() => {}}
        focusTrapZoneProps={{ disableFirstFocus: true }}
        data-testid={CalcFeatureFormTestId}
      >
        <CalculationFeatureForm
          calculationFeature={selectedFeature}
          onCancel={onFormPanelDismissed}
          calcTypes={calcTypes}
          contract={contract}
          store={store}
          onSubmit={handleSubmit}
          data-testid={CalcFeatureFormTestId}
        />
      </Panel>
    </>
  );
};

CalculationFeatures.propTypes = {
  title: PropTypes.string.isRequired,
  continueTo: PropTypes.string,
};

CalculationFeatures.defaultProps = {
  continueTo: '',
};

export default CalculationFeatures;
